import { addBits, isBitmaskMatching } from '../Common/Utils/BitwiseOperations'
import {
    REPORTVIEW_FLAGS_BLF_AUTOMATIC_TEXT_TRUNCATION_ADJUSTMENTS,
    REPORTVIEW_FLAGS_BLF_SHOW_LARGE_TOOLTIPS,
    REPORTVIEW_FLAGS_PRINT_RPTINFO,
    REPORTVIEW_FLAGS_TABLE_SHOW_CONTENT_ROWS,
    REPORTVIEW_FLAGS_TABLE_SHOW_FOOTER_ROW,
    REPORTVIEW_FLAGS_TABLE_SHOW_HEADER_ROW,
    REPORTVIEW_FLAGS_TABLE_SHOW_SPACER_ROWS
} from '../Common/Constants/ReportViewDisplayBitFlags'
import {
    RptConfigurationDataAddDto,
    RptConfigurationDataRequestDto,
    RptConfigurationDataResponseDto,
    RptConfigurationDataUpdateDto
} from '../Common/Types/Types'
import {
    FINANCIAL_DECIMAL_SEPARATOR_OPTION_COLON,
    FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA,
    FINANCIAL_DECIMAL_SEPARATOR_OPTION_FULL_STOP,
    FINANCIAL_GROUPINGSEPARATOR_OPTION_COMMA,
    FINANCIAL_GROUPINGSEPARATOR_OPTION_FULL_STOP,
    FINANCIAL_GROUPINGSEPARATOR_OPTION_SPACE
} from './FinancialTab/Constants'
import { ACDNACodeValue } from './Types'
import { NOT_DEFINED_VALUE_STRING } from '../Common/Constants/DefaultValues'
import {
    FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY,
    FILTER_CHANNEL_TYPE_FILTER_OPTION_CHAT,
    FILTER_CHANNEL_TYPE_FILTER_OPTION_EMAIL,
    FILTER_CHANNEL_TYPE_FILTER_OPTION_VOICE
} from './FilterTab/Constants'
import { DaysOfTheWeek as DaysOfTheWeekLanguage } from '../../../i18n'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { StatisticsConfiguration } from './FieldsTab/Types'
import { DashboardLayoutReportQuantities, DashboardLayoutStyle, ReportListItemType } from './DashboardTab/Types'
import {
    mapStatisticsConfigurationsToStatisticsSettingsString,
    STATISTICS_SETTINGS_LIST_SEPARATOR
} from '../Common/Utils/ReportStatistics'
import { FOLDERS_SEPARATOR_BACKEND } from '../MainMenu/ReportList/Constants'
import { IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { ReportListItemResponse } from '../../NonReact/common/Types/Types'

// Constants used for mappings
const FILTER_CHANNEL_TYPE_FILTER_VOICE_BITMASK = 0x01
const FILTER_CHANNEL_TYPE_FILTER_CHAT_BITMASK = 0x02
const FILTER_CHANNEL_TYPE_FILTER_EMAIL_BITMASK = 0x04
const FILTER_CHANNEL_TYPE_FILTER_ANY_BITMASK = 0xffff

const DATETIME_DAYS_BITMAP_MONDAY = 0x01
const DATETIME_DAYS_BITMAP_TUESDAY = 0x02
const DATETIME_DAYS_BITMAP_WEDNESDAY = 0x04
const DATETIME_DAYS_BITMAP_THURSDAY = 0x08
const DATETIME_DAYS_BITMAP_FRIDAY = 0x10
const DATETIME_DAYS_BITMAP_SATURDAY = 0x20
const DATETIME_DAYS_BITMAP_SUNDAY = 0x40
const DATETIME_DAYS_BITMAP_NONE = 0
// const DATETIME_DAYS_ALL = 0x7f

const DATETIME_DAYS_MONDAY_API_VALUE = 1
const DATETIME_DAYS_TUESDAY_API_VALUE = 2
const DATETIME_DAYS_WEDNESDAY_API_VALUE = 3
const DATETIME_DAYS_THURSDAY_API_VALUE = 4
const DATETIME_DAYS_FRIDAY_API_VALUE = 5
const DATETIME_DAYS_SATURDAY_API_VALUE = 6
const DATETIME_DAYS_SUNDAY_API_VALUE = 7

// Statistics settings values for Dashboard report type.
const LAYOUT_HORIZONTAL_SPLIT_STATISTICS_SETTINGS_VALUE = '0;0;1;1;1;1,0;1;1;1;1;1'
const LAYOUT_1_LEFT_2_MIDDLE_2_RIGHT_STATISTICS_SETTINGS_VALUE =
    '0;0;1;2;1;1,1;0;1;1;1;1,2;0;1;1;1;1,1;1;1;1;1;1,2;1;1;1;1;1'
const LAYOUT_1_LEFT_2_RIGHT_STATISTICS_SETTINGS_VALUE = '0;0;1;2;1;1,1;0;1;1;1;1,1;1;1;1;1;1'
const LAYOUT_1_TOP_2_BOTTOM_STATISTICS_SETTINGS_VALUE = '0;0;2;1;1;1,0;1;1;1;1;1,1;1;1;1;1;1'
const LAYOUT_1_TOP_3_BOTTOM_STATISTICS_SETTINGS_VALUE = '0;0;3;1;1;1,0;1;1;1;1;1,1;1;1;1;1;1,2;1;1;1;1;1'
const LAYOUT_1X1_STATISTICS_SETTINGS_VALUE = '0;0;1;1;1;1'
const LAYOUT_2_TOP_3_BOTTOM_STATISTICS_SETTINGS_VALUE = '0;0;3;1;1;1,3;0;3;1;1;1,0;1;2;1;1;1,2;1;2;1;1;1,4;1;2;1;1;1'
const LAYOUT_2_X_2_STATISTICS_SETTINGS_VALUE = '0;0;1;1;1;1,1;0;1;1;1;1,0;1;1;1;1;1,1;1;1;1;1;1'
const LAYOUT_3_X_1_STATISTICS_SETTINGS_VALUE = '0;0;1;1;1;1,1;0;1;1;1;1,2;0;1;1;1;1'
const LAYOUT_3_X_2_STATISTICS_SETTINGS_VALUE = '0;0;1;1;1;1,1;0;1;1;1;1,2;0;1;1;1;1,0;1;1;1;1;1,1;1;1;1;1;1,2;1;1;1;1;1'
const LAYOUT_VERTICAL_SPLIT_STATISTICS_SETTINGS_VALUE = '0;0;1;1;1;1,1;0;1;1;1;1'

// ReportViewFlags mapping
export const mapResponseToShowingHeaderRowEnabled = (rptConfigurationData: RptConfigurationDataResponseDto): boolean =>
    isBitmaskMatching(rptConfigurationData.ReportViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_HEADER_ROW)
export const mapResponseToShowingFooterRowEnabled = (rptConfigurationData: RptConfigurationDataResponseDto): boolean =>
    isBitmaskMatching(rptConfigurationData.ReportViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_FOOTER_ROW)
export const mapResponseToShowingContentRowsEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean => isBitmaskMatching(rptConfigurationData.ReportViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_CONTENT_ROWS)
export const mapResponseToShowingSpacerRowsEnabled = (rptConfigurationData: RptConfigurationDataResponseDto): boolean =>
    isBitmaskMatching(rptConfigurationData.ReportViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_SPACER_ROWS)
export const mapResponseToShowingLargeBLFToolTipsEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean => isBitmaskMatching(rptConfigurationData.ReportViewFlags, REPORTVIEW_FLAGS_BLF_SHOW_LARGE_TOOLTIPS)
export const mapResponseToAutomaticTextTruncationEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean =>
    isBitmaskMatching(rptConfigurationData.ReportViewFlags, REPORTVIEW_FLAGS_BLF_AUTOMATIC_TEXT_TRUNCATION_ADJUSTMENTS)

export const mapStatePropertiesToReportViewFlags = (
    isShowingHeaderRowEnabled: boolean,
    isShowingFooterRowEnabled: boolean,
    isShowingContentRowsEnabled: boolean,
    isShowingSpacerRowsEnabled: boolean,
    isShowingLargeBLFToolTipsEnabled: boolean,
    isAutomaticTextTruncationEnabled: boolean
): number => {
    let flagsValue = 0
    if (isShowingHeaderRowEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_HEADER_ROW)
    if (isShowingFooterRowEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_FOOTER_ROW)
    if (isShowingContentRowsEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_CONTENT_ROWS)
    if (isShowingSpacerRowsEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_SPACER_ROWS)
    if (isShowingLargeBLFToolTipsEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_BLF_SHOW_LARGE_TOOLTIPS)
    if (isAutomaticTextTruncationEnabled)
        flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_BLF_AUTOMATIC_TEXT_TRUNCATION_ADJUSTMENTS)
    return flagsValue
}

// Filter mappings
export const mapResponseFilterCallChannelsBitmapToChannelTypes = (filterCallChannels: number): number[] => [
    ...(isBitmaskMatching(filterCallChannels, FILTER_CHANNEL_TYPE_FILTER_ANY_BITMASK)
        ? [FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY]
        : [
              ...(isBitmaskMatching(filterCallChannels, FILTER_CHANNEL_TYPE_FILTER_EMAIL_BITMASK)
                  ? [FILTER_CHANNEL_TYPE_FILTER_OPTION_EMAIL]
                  : []),
              ...(isBitmaskMatching(filterCallChannels, FILTER_CHANNEL_TYPE_FILTER_VOICE_BITMASK)
                  ? [FILTER_CHANNEL_TYPE_FILTER_OPTION_VOICE]
                  : []),
              ...(isBitmaskMatching(filterCallChannels, FILTER_CHANNEL_TYPE_FILTER_CHAT_BITMASK)
                  ? [FILTER_CHANNEL_TYPE_FILTER_OPTION_CHAT]
                  : [])
          ])
]

export const mapStateChannelTypesValuesToRequestFilterChannels = (channelTypes: number[]): string => {
    // If ANY option is selected, we send ANY bitmask.
    if (channelTypes.find((value) => value === FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY)) {
        return String(FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY)
    }
    // Otherwise, we need to return all selected options as comma delimited string.
    return channelTypes.join(',')
}

// PrintViewFlags mappings
export const mapResponseToIsPrintShowingHeaderRowEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean => isBitmaskMatching(rptConfigurationData.PrintViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_HEADER_ROW)
export const mapResponseToIsPrintShowingFooterRowEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean => isBitmaskMatching(rptConfigurationData.PrintViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_FOOTER_ROW)
export const mapResponseToIsPrintShowingContentRowsEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean => isBitmaskMatching(rptConfigurationData.PrintViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_CONTENT_ROWS)
export const mapResponseToIsPrintShowingSpacerRowsEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean => isBitmaskMatching(rptConfigurationData.PrintViewFlags, REPORTVIEW_FLAGS_TABLE_SHOW_SPACER_ROWS)
export const mapResponseToIsShowSummaryHeaderEnabled = (
    rptConfigurationData: RptConfigurationDataResponseDto
): boolean => isBitmaskMatching(rptConfigurationData.PrintViewFlags, REPORTVIEW_FLAGS_PRINT_RPTINFO)

export const mapStatePropertiesToPrintViewFlags = (
    isPrintShowingHeaderRowEnabled: boolean,
    isPrintShowingFooterRowEnabled: boolean,
    isPrintShowingContentRowsEnabled: boolean,
    isPrintShowingSpacerRowsEnabled: boolean,
    isPrintShowSummaryHeaderEnabled: boolean
): number => {
    let flagsValue = 0
    if (isPrintShowingHeaderRowEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_HEADER_ROW)
    if (isPrintShowingFooterRowEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_FOOTER_ROW)
    if (isPrintShowingContentRowsEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_CONTENT_ROWS)
    if (isPrintShowingSpacerRowsEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_TABLE_SHOW_SPACER_ROWS)
    if (isPrintShowSummaryHeaderEnabled) flagsValue = addBits(flagsValue, REPORTVIEW_FLAGS_PRINT_RPTINFO)
    return flagsValue
}

/**
 * Maps day name short value to API value used for single day.
 *
 * @param dayOfTheWeekString Short name of the day
 * @param language Resource where the name of the day is searched for.
 */
export const mapDaysStateStringValueToDayApiValue = (
    dayOfTheWeekString: string,
    language: CLanguageHandler
): number => {
    switch (dayOfTheWeekString) {
        case `${language?.get(DaysOfTheWeekLanguage.Mondayshort)}`:
            return DATETIME_DAYS_MONDAY_API_VALUE
        case `${language?.get(DaysOfTheWeekLanguage.Tuesdayshort)}`:
            return DATETIME_DAYS_TUESDAY_API_VALUE
        case `${language?.get(DaysOfTheWeekLanguage.Wednesdayshort)}`:
            return DATETIME_DAYS_WEDNESDAY_API_VALUE
        case `${language?.get(DaysOfTheWeekLanguage.Thursdayshort)}`:
            return DATETIME_DAYS_THURSDAY_API_VALUE
        case `${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`:
            return DATETIME_DAYS_FRIDAY_API_VALUE
        case `${language?.get(DaysOfTheWeekLanguage.Saturdayshort)}`:
            return DATETIME_DAYS_SATURDAY_API_VALUE
        case `${language?.get(DaysOfTheWeekLanguage.Sundayshort)}`:
            return DATETIME_DAYS_SUNDAY_API_VALUE
        default:
            return 0
    }
}

// days of the week
/**
 * Maps multiple short day name values to API bitmap value used for multi-day fields, e.g. filters.
 *
 * @param daysOfTheWeek Array with short name of the days.
 * @param language Resource where the name of the day is searched for.
 */
export const mapDaysStatePropertyToDaysOfWeekBitmap = (daysOfTheWeek: string[], language: CLanguageHandler): number => {
    const daysOfTheWeekResult = daysOfTheWeek
        .map((dayOfTheWeekStringValue: string): number => {
            switch (dayOfTheWeekStringValue) {
                case `${language?.get(DaysOfTheWeekLanguage.Mondayshort)}`:
                    return DATETIME_DAYS_BITMAP_MONDAY
                case `${language?.get(DaysOfTheWeekLanguage.Tuesdayshort)}`:
                    return DATETIME_DAYS_BITMAP_TUESDAY
                case `${language?.get(DaysOfTheWeekLanguage.Wednesdayshort)}`:
                    return DATETIME_DAYS_BITMAP_WEDNESDAY
                case `${language?.get(DaysOfTheWeekLanguage.Thursdayshort)}`:
                    return DATETIME_DAYS_BITMAP_THURSDAY
                case `${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`:
                    return DATETIME_DAYS_BITMAP_FRIDAY
                case `${language?.get(DaysOfTheWeekLanguage.Saturdayshort)}`:
                    return DATETIME_DAYS_BITMAP_SATURDAY
                case `${language?.get(DaysOfTheWeekLanguage.Sundayshort)}`:
                    return DATETIME_DAYS_BITMAP_SUNDAY
                default:
                    return DATETIME_DAYS_BITMAP_NONE
            }
        })
        .reduce((prevValue, currentValue) => prevValue | currentValue, 0)
    return daysOfTheWeekResult
}

/**
 * Maps API bitmap value used for multi-day fields into an array of short day name values.
 *
 * @param daysOfTheWeek Bitmap represented as number.
 * @param language Resource where the name of the day is searched for.
 */
export const mapResponseDaysOfTheWeekToDaysOfWeekArray = (
    daysOfTheWeek: number,
    language: CLanguageHandler
): string[] => [
    ...(isBitmaskMatching(daysOfTheWeek, DATETIME_DAYS_BITMAP_MONDAY)
        ? [`${language?.get(DaysOfTheWeekLanguage.Mondayshort)}`]
        : []),
    ...(isBitmaskMatching(daysOfTheWeek, DATETIME_DAYS_BITMAP_TUESDAY)
        ? [`${language?.get(DaysOfTheWeekLanguage.Tuesdayshort)}`]
        : []),
    ...(isBitmaskMatching(daysOfTheWeek, DATETIME_DAYS_BITMAP_WEDNESDAY)
        ? [`${language?.get(DaysOfTheWeekLanguage.Wednesdayshort)}`]
        : []),
    ...(isBitmaskMatching(daysOfTheWeek, DATETIME_DAYS_BITMAP_THURSDAY)
        ? [`${language?.get(DaysOfTheWeekLanguage.Thursdayshort)}`]
        : []),
    ...(isBitmaskMatching(daysOfTheWeek, DATETIME_DAYS_BITMAP_FRIDAY)
        ? [`${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`]
        : []),
    ...(isBitmaskMatching(daysOfTheWeek, DATETIME_DAYS_BITMAP_SATURDAY)
        ? [`${language?.get(DaysOfTheWeekLanguage.Saturdayshort)}`]
        : []),
    ...(isBitmaskMatching(daysOfTheWeek, DATETIME_DAYS_BITMAP_SUNDAY)
        ? [`${language?.get(DaysOfTheWeekLanguage.Sundayshort)}`]
        : [])
]

/**
 * Maps single day API value to short version of the day name used within UI.
 *
 * @param dayOfTheWeek API value for day of the week.
 * @param language Resource where the name of the day is searched for.
 */
export const mapResponseDayOfTheWeekToDayOfWeekString = (dayOfTheWeek: number, language: CLanguageHandler): string => {
    switch (dayOfTheWeek) {
        case DATETIME_DAYS_MONDAY_API_VALUE:
            return `${language?.get(DaysOfTheWeekLanguage.Mondayshort)}`
        case DATETIME_DAYS_TUESDAY_API_VALUE:
            return `${language?.get(DaysOfTheWeekLanguage.Tuesdayshort)}`
        case DATETIME_DAYS_WEDNESDAY_API_VALUE:
            return `${language?.get(DaysOfTheWeekLanguage.Wednesdayshort)}`
        case DATETIME_DAYS_THURSDAY_API_VALUE:
            return `${language?.get(DaysOfTheWeekLanguage.Thursdayshort)}`
        case DATETIME_DAYS_FRIDAY_API_VALUE:
            return `${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`
        case DATETIME_DAYS_SATURDAY_API_VALUE:
            return `${language?.get(DaysOfTheWeekLanguage.Saturdayshort)}`
        case DATETIME_DAYS_SUNDAY_API_VALUE:
            return `${language?.get(DaysOfTheWeekLanguage.Sundayshort)}`
        default:
            return ''
    }
}

// Financial values
export const mapFinancialGroupingSeparatorToStringValue = (value: number): string => {
    switch (value) {
        case FINANCIAL_GROUPINGSEPARATOR_OPTION_FULL_STOP:
            return '.'
        case FINANCIAL_GROUPINGSEPARATOR_OPTION_SPACE:
            return ' '
        case FINANCIAL_GROUPINGSEPARATOR_OPTION_COMMA:
        default:
            return ','
    }
}

export const mapFinancialDecimalSeparatorToStringValue = (value: number): string => {
    switch (value) {
        case FINANCIAL_DECIMAL_SEPARATOR_OPTION_COMMA:
            return ','
        case FINANCIAL_DECIMAL_SEPARATOR_OPTION_COLON:
            return ':'
        case FINANCIAL_DECIMAL_SEPARATOR_OPTION_FULL_STOP:
        default:
            return '.'
    }
}

/**
 * Maps the ACD N/A code value from the list of all options.
 *
 * @param ACDNACodeValues List of ACD N/A codes.
 * @param codeIdentifier The code identifier.
 */
export const mapACDNACode = (ACDNACodeValues: ACDNACodeValue[], codeIdentifier: number): string => {
    const targetACDNACodeValue = ACDNACodeValues.find(
        (acdnaCode: ACDNACodeValue): boolean => acdnaCode.codeIdentifier === codeIdentifier
    )

    return !targetACDNACodeValue || targetACDNACodeValue.codeValue === NOT_DEFINED_VALUE_STRING
        ? '' // The API uses empty string '' instead of '[Not Defined]'
        : targetACDNACodeValue.codeValue
}

const mapRptConfigurationDataDtoToFormData = (
    rptConfigurationDataUpdateDto: RptConfigurationDataRequestDto
): FormData => {
    const formData = new FormData()
    formData.append('IsDescriptionChanged', 'true')

    // formData.append('RefreshView',rptConfigurationDataUpdateDto.RefreshView );
    // formData.append('ResType',rptConfigurationDataUpdateDto.ResType );
    // formData.append('IsDescriptionChanged',rptConfigurationDataUpdateDto.IsDescriptionChanged );
    // formData.append('IsSetCallsInAnsThresChanged',rptConfigurationDataUpdateDto.IsSetCallsInAnsThresChanged );
    formData.append('Duration', String(rptConfigurationDataUpdateDto.Duration))
    // formData.append('DurationIsUTC',rptConfigurationDataUpdateDto.DurationIsUTC );
    formData.append('DurationCustomStart', rptConfigurationDataUpdateDto.DurationCustomStart)
    formData.append('DurationCustomEnd', rptConfigurationDataUpdateDto.DurationCustomEnd)
    formData.append('DurationDaysOfWeek', String(rptConfigurationDataUpdateDto.DurationDaysOfWeek))
    formData.append('DurationTimeOfDayStart', rptConfigurationDataUpdateDto.DurationTimeOfDayStart)
    formData.append('DurationTimeOfDayEnd', rptConfigurationDataUpdateDto.DurationTimeOfDayEnd)
    formData.append('DurationDayCount', String(rptConfigurationDataUpdateDto.DurationDayCount))
    // formData.append('AutoRunAfterAdd',rptConfigurationDataUpdateDto.AutoRunAfterAdd );
    formData.append('Description', rptConfigurationDataUpdateDto.Description)
    if (isVarNotEmpty(rptConfigurationDataUpdateDto.CustomIntervalValue)) {
        formData.append('CustomIntervalValue', String(rptConfigurationDataUpdateDto.CustomIntervalValue))
    }
    if (isVarNotEmpty(rptConfigurationDataUpdateDto.CustomIntervalType)) {
        formData.append('CustomIntervalType', String(rptConfigurationDataUpdateDto.CustomIntervalType))
    }
    if (isVarNotEmpty(rptConfigurationDataUpdateDto.FilterEntityType)) {
        formData.append('FilterEntityType', String(rptConfigurationDataUpdateDto.FilterEntityType))
    }
    formData.append('Information', rptConfigurationDataUpdateDto.Information)
    formData.append('ZoomLevel', String(rptConfigurationDataUpdateDto.ZoomLevel))
    formData.append('ReportViewType', String(rptConfigurationDataUpdateDto.ReportViewType))
    formData.append('ReportViewFlags', String(rptConfigurationDataUpdateDto.ReportViewFlags))
    formData.append('PrintViewChartInclusion', String(rptConfigurationDataUpdateDto.PrintViewChartInclusion))
    formData.append('PrintViewFlags', String(rptConfigurationDataUpdateDto.PrintViewFlags))
    formData.append('WallboardTileType', String(rptConfigurationDataUpdateDto.WallboardTileType))
    formData.append('ChartProvider', String(rptConfigurationDataUpdateDto.ChartProvider))
    formData.append('ChartType', String(rptConfigurationDataUpdateDto.ChartType))
    formData.append('ExternalContentURL', rptConfigurationDataUpdateDto.ExternalContentURL)
    // formData.append('IsCustom',rptConfigurationDataUpdateDto.IsCustom );
    // formData.append('CustomDefClassID',rptConfigurationDataUpdateDto.CustomDefClassID );
    // formData.append('CustomContentClassID',rptConfigurationDataUpdateDto.CustomContentClassID );
    formData.append('Type', String(rptConfigurationDataUpdateDto.Type))
    // formData.append('FilterIDDb', rptConfigurationDataUpdateDto.FilterIDDb)
    // formData.append('FilterIDTimeSync', rptConfigurationDataUpdateDto.FilterIDTimeSync)
    formData.append('FilterCallIDThread', rptConfigurationDataUpdateDto.FilterCallIDThread)
    // formData.append('FilterCallIDThreadWCMatches', rptConfigurationDataUpdateDto.FilterCallIDThreadWCMatches)
    formData.append('FilterCallIDTelSys', rptConfigurationDataUpdateDto.FilterCallIDTelSys)
    // formData.append('FilterCallIDTelSysWCMatches', String(rptConfigurationDataUpdateDto.FilterCallIDTelSysWCMatches))
    formData.append('FilterTelephoneSystemID', String(rptConfigurationDataUpdateDto.FilterTelephoneSystemID))
    formData.append('FilterPartitionIDs', rptConfigurationDataUpdateDto.FilterPartitionIDs)
    formData.append('FilterGroupIDs', rptConfigurationDataUpdateDto.FilterGroupIDs ?? '')
    formData.append('FilterTelNos', rptConfigurationDataUpdateDto.FilterTelNos)
    formData.append('FilterTelNoDescriptions', rptConfigurationDataUpdateDto.FilterTelNoDescriptions)
    formData.append('FilterDevices', rptConfigurationDataUpdateDto.FilterDevices)
    formData.append('FilterDeviceTypes', rptConfigurationDataUpdateDto.FilterDeviceTypes)
    formData.append(
        'FilterDevicesIfGrpIncGrpMmbrCalls',
        String(rptConfigurationDataUpdateDto.FilterDevicesIfGrpIncGrpMmbrCalls)
    )
    formData.append(
        'FilterDevicesIfGrpIncACDItemsForGrpDevOnly',
        String(rptConfigurationDataUpdateDto.FilterDevicesIfGrpIncACDItemsForGrpDevOnly)
    )
    formData.append('FilterACDAgents', rptConfigurationDataUpdateDto.FilterACDAgents)
    formData.append('FilterLocation', String(rptConfigurationDataUpdateDto.FilterLocation))
    formData.append('FilterCallChannels', String(rptConfigurationDataUpdateDto.FilterCallChannels))
    formData.append('FilterSubject', rptConfigurationDataUpdateDto.FilterSubject)
    formData.append('FilterCallType', String(rptConfigurationDataUpdateDto.FilterCallType))
    formData.append(
        'FilterCallTypeConfCallAsExternal',
        String(rptConfigurationDataUpdateDto.FilterCallTypeConfCallAsExternal)
    )
    formData.append('FilterAnsType', String(rptConfigurationDataUpdateDto.FilterAnsType))
    formData.append('FilterCLIType', String(rptConfigurationDataUpdateDto.FilterCLIType))
    formData.append('FilterDDIDigits', rptConfigurationDataUpdateDto.FilterDDIDigits)
    formData.append('FilterAccountCodes', rptConfigurationDataUpdateDto.FilterAccountCodes)
    formData.append('FilterMovedToTelNos', rptConfigurationDataUpdateDto.FilterMovedToTelNos)
    formData.append('FilterMovedFromDevices', rptConfigurationDataUpdateDto.FilterMovedFromDevices)
    formData.append('FilterMovedFromACDAgents', rptConfigurationDataUpdateDto.FilterMovedFromACDAgents)
    formData.append('FilterMovedSegNo', String(rptConfigurationDataUpdateDto.FilterMovedSegNo))
    formData.append('FilterMovedSegReasonStarted', String(rptConfigurationDataUpdateDto.FilterMovedSegReasonStarted))
    formData.append('FilterMovedSegReasonEnded', String(rptConfigurationDataUpdateDto.FilterMovedSegReasonEnded))
    formData.append('IncludeOutboundCalls', String(rptConfigurationDataUpdateDto.IncludeOutboundCalls))
    formData.append(
        'IncludeOutboundCallsFromDeviceList',
        rptConfigurationDataUpdateDto.IncludeOutboundCallsFromDeviceList
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeInboundEscapedTo',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeInboundEscapedTo)
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeInboundDivertedOrOverflowedTo',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeInboundDivertedOrOverflowedTo)
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeInboundTransferredTo',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeInboundTransferredTo)
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeAllOtherInbound',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeAllOtherInbound)
    )
    formData.append('IncludeOutboundCalls', String(rptConfigurationDataUpdateDto.IncludeOutboundCalls))
    formData.append(
        'IncludeOutboundCallsFromDeviceList',
        rptConfigurationDataUpdateDto.IncludeOutboundCallsFromDeviceList
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeInboundEscapedTo',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeInboundEscapedTo)
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeInboundDivertedOrOverflowedTo',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeInboundDivertedOrOverflowedTo)
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeInboundTransferredTo',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeInboundTransferredTo)
    )
    formData.append(
        'FilterUnreturnedLostCallsExcludeAllOtherInbound',
        String(rptConfigurationDataUpdateDto.FilterUnreturnedLostCallsExcludeAllOtherInbound)
    )
    formData.append('IncludeOutboundCalls', String(rptConfigurationDataUpdateDto.IncludeOutboundCalls))
    formData.append(
        'IncludeOutboundCallsFromDeviceList',
        rptConfigurationDataUpdateDto.IncludeOutboundCallsFromDeviceList
    )
    formData.append(
        'StatisticSettingSvcLvlAnsThres',
        String(rptConfigurationDataUpdateDto.StatisticSettingSvcLvlAnsThres)
    )
    formData.append(
        'StatisticSettingSvcLvlAbndThres',
        String(rptConfigurationDataUpdateDto.StatisticSettingSvcLvlAbndThres)
    )
    formData.append(
        'StatisticSettingSvcLvlIgnQckAbndCalls',
        String(rptConfigurationDataUpdateDto.StatisticSettingSvcLvlIgnQckAbndCalls)
    )
    formData.append(
        'StatisticSettingSvcLvlQckAbndCallsThres',
        String(rptConfigurationDataUpdateDto.StatisticSettingSvcLvlQckAbndCallsThres)
    )
    formData.append(
        'StatisticSettingSvcLvlIgnOvrfldCalls',
        String(rptConfigurationDataUpdateDto.StatisticSettingSvcLvlIgnOvrfldCalls)
    )
    formData.append(
        'StatisticSettingSvcLvlOvrfldCallsThres',
        String(rptConfigurationDataUpdateDto.StatisticSettingSvcLvlOvrfldCallsThres)
    )
    formData.append(
        'StatisticSettingCallsInIgnQckAbndCalls',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInIgnQckAbndCalls)
    )
    formData.append(
        'StatisticSettingCallsInQckAbndCallsThres',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInQckAbndCallsThres)
    )
    formData.append(
        'StatisticSettingCallsInIgnOvrfldCalls',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInIgnOvrfldCalls)
    )
    formData.append(
        'StatisticSettingCallsInOvrfldCallsThres',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInOvrfldCallsThres)
    )
    formData.append(
        'StatisticSettingUnreturnedLostCallsIncludeEndedOnHold',
        String(rptConfigurationDataUpdateDto.StatisticSettingUnreturnedLostCallsIncludeEndedOnHold)
    )
    formData.append(
        'StatisticSettingUnreturnedLostCallsIncludeEscaped',
        String(rptConfigurationDataUpdateDto.StatisticSettingUnreturnedLostCallsIncludeEscaped)
    )
    formData.append(
        'StatisticSettingExcludeHeldTimeFromTalkTime',
        String(rptConfigurationDataUpdateDto.StatisticSettingExcludeHeldTimeFromTalkTime)
    )
    formData.append(
        'StatisticSettingCallsInAnsThres1',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAnsThres1)
    )
    formData.append(
        'StatisticSettingCallsInAnsThres2',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAnsThres2)
    )
    formData.append(
        'StatisticSettingCallsInAnsThres3',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAnsThres3)
    )
    formData.append(
        'StatisticSettingCallsInAnsThres4',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAnsThres4)
    )
    formData.append(
        'StatisticSettingCallsInAnsThres5',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAnsThres5)
    )
    formData.append(
        'StatisticSettingCallsInAnsThres6',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAnsThres6)
    )
    formData.append(
        'StatisticSettingCallsInAbndThres1',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAbndThres1)
    )
    formData.append(
        'StatisticSettingCallsInAbndThres2',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAbndThres2)
    )
    formData.append(
        'StatisticSettingCallsInAbndThres3',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAbndThres3)
    )
    formData.append(
        'StatisticSettingCallsInAbndThres4',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAbndThres4)
    )
    formData.append(
        'StatisticSettingCallsInAbndThres5',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAbndThres5)
    )
    formData.append(
        'StatisticSettingCallsInAbndThres6',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInAbndThres6)
    )
    formData.append(
        'StatisticSettingMvdToUnResDevsAreOvrfldCalls',
        String(rptConfigurationDataUpdateDto.StatisticSettingMvdToUnResDevsAreOvrfldCalls)
    )
    formData.append('MaxRows', String(rptConfigurationDataUpdateDto.MaxRows))
    formData.append('MaxFilterEntities', String(rptConfigurationDataUpdateDto.MaxFilterEntities))
    formData.append('FormatCSVStatValuesType', String(rptConfigurationDataUpdateDto.FormatCSVStatValuesType))
    formData.append('DebugContentDump', String(rptConfigurationDataUpdateDto.DebugContentDump))
    formData.append('AlarmACDAvailableOn', String(rptConfigurationDataUpdateDto.AlarmACDAvailableOn))
    formData.append('AlarmACDAvailableThresText', String(rptConfigurationDataUpdateDto.AlarmACDAvailableThres))
    formData.append('AlarmACDBusyOn', String(rptConfigurationDataUpdateDto.AlarmACDBusyOn))
    formData.append('AlarmACDBusyThresText', String(rptConfigurationDataUpdateDto.AlarmACDBusyThres))
    formData.append('AlarmACDNADNDOn', String(rptConfigurationDataUpdateDto.AlarmACDNADNDOn))
    formData.append('AlarmACDNADNDThresText', String(rptConfigurationDataUpdateDto.AlarmACDNADNDThres))
    formData.append('AlarmACDWrapUpOn', String(rptConfigurationDataUpdateDto.AlarmACDWrapUpOn))
    formData.append('AlarmACDWrapUpThresText', String(rptConfigurationDataUpdateDto.AlarmACDWrapUpThres))
    formData.append('AlarmCallAlertOn', String(rptConfigurationDataUpdateDto.AlarmCallAlertOn))
    formData.append('AlarmCallAlertThresText', String(rptConfigurationDataUpdateDto.AlarmCallAlertThres))
    formData.append('AlarmCallTalkOn', String(rptConfigurationDataUpdateDto.AlarmCallTalkOn))
    formData.append('AlarmCallTalkThresText', String(rptConfigurationDataUpdateDto.AlarmCallTalkThres))
    formData.append('AlarmCallLostOn', String(rptConfigurationDataUpdateDto.AlarmCallLostOn))
    formData.append('AlarmFirePlaySound', String(rptConfigurationDataUpdateDto.AlarmFirePlaySound))
    formData.append('AlarmFireActivateWin', String(rptConfigurationDataUpdateDto.AlarmFireActivateWin))
    formData.append('FilterCallTimeRngAllCmp', String(rptConfigurationDataUpdateDto.FilterCallTimeRngAllCmp))
    formData.append('FilterCallTimeRngAllValue1Text', rptConfigurationDataUpdateDto.FilterCallTimeRngAllValue1Text)
    formData.append('FilterCallTimeRngAllValue2Text', rptConfigurationDataUpdateDto.FilterCallTimeRngAllValue2Text)
    formData.append('FilterCallTimeRngSegCmp', String(rptConfigurationDataUpdateDto.FilterCallTimeRngSegCmp))
    formData.append('FilterCallTimeRngSegValue1Text', rptConfigurationDataUpdateDto.FilterCallTimeRngSegValue1Text)
    formData.append('FilterCallTimeRngSegValue2Text', rptConfigurationDataUpdateDto.FilterCallTimeRngSegValue2Text)
    formData.append('FilterCallTimeTalkCmp', String(rptConfigurationDataUpdateDto.FilterCallTimeTalkCmp))
    formData.append('FilterCallTimeTalkValue1Text', rptConfigurationDataUpdateDto.FilterCallTimeTalkValue1Text)
    formData.append('FilterCallTimeTalkValue2Text', rptConfigurationDataUpdateDto.FilterCallTimeTalkValue2Text)
    formData.append('FilterCallTimeAllCmp', String(rptConfigurationDataUpdateDto.FilterCallTimeAllCmp))
    formData.append('FilterCallTimeAllValue1Text', rptConfigurationDataUpdateDto.FilterCallTimeAllValue1Text)
    formData.append('FilterCallTimeAllValue2Text', rptConfigurationDataUpdateDto.FilterCallTimeAllValue2Text)
    formData.append('FilterCallTimeHeldCmp', String(rptConfigurationDataUpdateDto.FilterCallTimeHeldCmp))
    formData.append('FilterCallTimeHeldValue1Text', rptConfigurationDataUpdateDto.FilterCallTimeHeldValue1Text)
    formData.append('FilterCallTimeHeldValue2Text', rptConfigurationDataUpdateDto.FilterCallTimeHeldValue2Text)

    formData.append('StatisticSettingACDNACode01', rptConfigurationDataUpdateDto.StatisticSettingACDNACode01)
    formData.append('StatisticSettingACDNACode02', rptConfigurationDataUpdateDto.StatisticSettingACDNACode02)
    formData.append('StatisticSettingACDNACode03', rptConfigurationDataUpdateDto.StatisticSettingACDNACode03)
    formData.append('StatisticSettingACDNACode04', rptConfigurationDataUpdateDto.StatisticSettingACDNACode04)
    formData.append('StatisticSettingACDNACode05', rptConfigurationDataUpdateDto.StatisticSettingACDNACode05)
    formData.append('StatisticSettingACDNACode06', rptConfigurationDataUpdateDto.StatisticSettingACDNACode06)
    formData.append('StatisticSettingACDNACode07', rptConfigurationDataUpdateDto.StatisticSettingACDNACode07)
    formData.append('StatisticSettingACDNACode08', rptConfigurationDataUpdateDto.StatisticSettingACDNACode08)
    formData.append('StatisticSettingACDNACode09', rptConfigurationDataUpdateDto.StatisticSettingACDNACode09)
    formData.append('StatisticSettingACDNACode10', rptConfigurationDataUpdateDto.StatisticSettingACDNACode10)
    formData.append('StatisticSettingACDNACode11', rptConfigurationDataUpdateDto.StatisticSettingACDNACode11)

    formData.append(
        'StatisticSettingACDNACodeUseCodeDesc',
        String(rptConfigurationDataUpdateDto.StatisticSettingACDNACodeUseCodeDesc)
    )
    formData.append(
        'StatisticSettingFinancialOrderValueInbound',
        rptConfigurationDataUpdateDto.StatisticSettingFinancialOrderValueInbound
    )
    formData.append(
        'StatisticSettingFinancialOrderValueOutbound',
        rptConfigurationDataUpdateDto.StatisticSettingFinancialOrderValueOutbound
    )
    formData.append(
        'StatisticSettingFinancialStrikeRateInbound',
        String(rptConfigurationDataUpdateDto.StatisticSettingFinancialStrikeRateInbound)
    )
    formData.append(
        'StatisticSettingFinancialStrikeRateOutbound',
        String(rptConfigurationDataUpdateDto.StatisticSettingFinancialStrikeRateOutbound)
    )
    formData.append(
        'StatisticSettingFinancialCurrencyID',
        String(rptConfigurationDataUpdateDto.StatisticSettingFinancialCurrencyID)
    )
    formData.append(
        'StatisticSettingFinancialCurrencyFormatPattern',
        rptConfigurationDataUpdateDto.StatisticSettingFinancialCurrencyFormatPattern
    )
    formData.append(
        'StatisticSettingFinancialCurrencyGroupingSeparator',
        rptConfigurationDataUpdateDto.StatisticSettingFinancialCurrencyGroupingSeparator
    )
    formData.append(
        'StatisticSettingFinancialCurrencyDecimalSeparator',
        rptConfigurationDataUpdateDto.StatisticSettingFinancialCurrencyDecimalSeparator
    )
    formData.append(
        'StatisticSettingCallsInIgnEscapedCalls',
        String(rptConfigurationDataUpdateDto.StatisticSettingCallsInIgnEscapedCalls)
    )

    if (rptConfigurationDataUpdateDto.schedule) {
        formData.append('ScheduleDelete', String(rptConfigurationDataUpdateDto.schedule.ScheduleDelete))
        formData.append('ScheduleReset', String(rptConfigurationDataUpdateDto.schedule.ScheduleReset))
        // formData.append('ScheduleIsActive',rptConfigurationDataUpdateDto.schedule.ScheduleIsActive );
        // formData.append('ScheduleUserUniqueID',rptConfigurationDataUpdateDto.schedule.ScheduleUserUniqueID );
        formData.append('ScheduleLocaleStr', rptConfigurationDataUpdateDto.schedule.ScheduleLocaleStr)
        formData.append('SchedulePaperSizeID', rptConfigurationDataUpdateDto.schedule.SchedulePaperSizeID)
        formData.append('ScheduleFormat', String(rptConfigurationDataUpdateDto.schedule.ScheduleFormat))
        formData.append('ScheduleType', String(rptConfigurationDataUpdateDto.schedule.ScheduleType))
        formData.append('ScheduleAtDate', rptConfigurationDataUpdateDto.schedule.ScheduleAtDate)
        formData.append('ScheduleAtDayOfWeek', String(rptConfigurationDataUpdateDto.schedule.ScheduleAtDayOfWeek))
        formData.append(
            'ScheduleAtDayOfWeekFilter',
            String(rptConfigurationDataUpdateDto.schedule.ScheduleAtDayOfWeekFilter)
        )
        formData.append('ScheduleAtDayOfMonth', String(rptConfigurationDataUpdateDto.schedule.ScheduleAtDayOfMonth))
        // formData.append('ScheduleAtMonthOfYear',rptConfigurationDataUpdateDto.schedule.ScheduleAtMonthOfYear );
        formData.append('ScheduleAtTime', rptConfigurationDataUpdateDto.schedule.ScheduleAtTime)
        formData.append(
            'ScheduleAtHourFilterStart',
            String(rptConfigurationDataUpdateDto.schedule.ScheduleAtHourFilterStart)
        )
        formData.append(
            'ScheduleAtHourFilterEnd',
            String(rptConfigurationDataUpdateDto.schedule.ScheduleAtHourFilterEnd)
        )
        formData.append('ScheduleAtMinute', String(rptConfigurationDataUpdateDto.schedule.ScheduleAtMinute))
        formData.append('ScheduleAutoDuration', String(rptConfigurationDataUpdateDto.schedule.ScheduleAutoDuration))
        formData.append('ScheduleBundleAsZIP', String(rptConfigurationDataUpdateDto.schedule.ScheduleBundleAsZIP))
        formData.append(
            'ScheduleMergeWithOtherSchedules',
            String(rptConfigurationDataUpdateDto.schedule.ScheduleMergeWithOtherSchedules)
        )
        formData.append('ScheduleMailingList', String(rptConfigurationDataUpdateDto.schedule.MailingList))
    }

    formData.append('IsAutoSwitched', String(rptConfigurationDataUpdateDto.IsAutoSwitched))
    formData.append('AutoSwitchIntervalDisplayText', rptConfigurationDataUpdateDto.AutoSwitchIntervalDisplayText)
    // formData.append(
    //     'AutoSwitchIntervalDisplayRealValue',
    //     String(rptConfigurationDataUpdateDto.AutoSwitchIntervalDisplayRealValue)
    // )
    formData.append('StatisticsDisplay', rptConfigurationDataUpdateDto.StatisticsDisplay)
    formData.append('StatisticsSettings', rptConfigurationDataUpdateDto.StatisticsSettings)
    // formData.append('InfoDurationFilterSummaryText',rptConfigurationDataUpdateDto.InfoDurationFilterSummaryText );
    return formData
}

export const mapRptConfigurationDataUpdateDtoToFormData = (
    rptConfigurationDataUpdateDto: RptConfigurationDataUpdateDto
): FormData => {
    const formData: FormData = mapRptConfigurationDataDtoToFormData(rptConfigurationDataUpdateDto)
    formData.append('ID', rptConfigurationDataUpdateDto.ID)
    return formData
}

export const mapRptConfigurationDataAddDtoToFormData = (
    rptConfigurationDataUpdateDto: RptConfigurationDataAddDto
): FormData => {
    const formData: FormData = mapRptConfigurationDataDtoToFormData(rptConfigurationDataUpdateDto)
    return formData
}

// Dashboard tab
export const mapConfiguredDashboardReportsToStatisticsDisplayString = (
    configuredDashboardReports: string[],
    selectedDashboardLayoutStyle: number
): string => {
    const quantity: number = DashboardLayoutReportQuantities[selectedDashboardLayoutStyle]
    const cdr: string[] = configuredDashboardReports
        // Remove duplicates if any.
        .filter(
            (configuredReportID, index, configuredDashboardReportIDsArray) =>
                !configuredReportID ||
                configuredReportID === '' ||
                configuredDashboardReportIDsArray.indexOf(configuredReportID) === index
        )

    if (cdr && cdr.length !== quantity) {
        const diff: number = quantity - cdr.length

        if (diff > 0) {
            return cdr.concat(new Array(diff).fill(undefined)).join(',')
        }
    }

    return cdr.join(STATISTICS_SETTINGS_LIST_SEPARATOR)
}

export const mapDashboardLayoutStyleToStatisticsSettingsString = (layoutStyle: DashboardLayoutStyle): string => {
    switch (layoutStyle) {
        case DashboardLayoutStyle.HorizontalSplit:
            return LAYOUT_HORIZONTAL_SPLIT_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout1Left2Middle2Right:
            return LAYOUT_1_LEFT_2_MIDDLE_2_RIGHT_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout1Left2Right:
            return LAYOUT_1_LEFT_2_RIGHT_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout1Top2Bottom:
            return LAYOUT_1_TOP_2_BOTTOM_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout1Top3Bottom:
            return LAYOUT_1_TOP_3_BOTTOM_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout1x1:
            return LAYOUT_1X1_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout2Top3Bottom:
            return LAYOUT_2_TOP_3_BOTTOM_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout2x2:
            return LAYOUT_2_X_2_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout3x1:
            return LAYOUT_3_X_1_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.Layout3x2:
            return LAYOUT_3_X_2_STATISTICS_SETTINGS_VALUE
        case DashboardLayoutStyle.VerticalSplit:
            return LAYOUT_VERTICAL_SPLIT_STATISTICS_SETTINGS_VALUE
        default:
            return ''
    }
}

export const mapStatisticsDisplayToListOfDashboardReports = (statisticsDisplay: string): string[] =>
    statisticsDisplay ? statisticsDisplay.split(STATISTICS_SETTINGS_LIST_SEPARATOR) : []

export const mapStatisticsSettingsToDashboardLayout = (statisticsSettings: string): DashboardLayoutStyle => {
    switch (statisticsSettings) {
        case LAYOUT_HORIZONTAL_SPLIT_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.HorizontalSplit
        case LAYOUT_1_LEFT_2_MIDDLE_2_RIGHT_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout1Left2Middle2Right
        case LAYOUT_1_LEFT_2_RIGHT_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout1Left2Right
        case LAYOUT_1_TOP_2_BOTTOM_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout1Top2Bottom
        case LAYOUT_1_TOP_3_BOTTOM_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout1Top3Bottom
        case LAYOUT_1X1_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout1x1
        case LAYOUT_2_TOP_3_BOTTOM_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout2Top3Bottom
        case LAYOUT_3_X_1_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout3x1
        case LAYOUT_2_X_2_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout2x2
        case LAYOUT_3_X_2_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.Layout3x2
        case LAYOUT_VERTICAL_SPLIT_STATISTICS_SETTINGS_VALUE:
            return DashboardLayoutStyle.VerticalSplit
        default:
            return DashboardLayoutStyle.Layout1x1
    }
}

// Fields tab
export const mapStatisticsConfigurationsToRequestStatisticsDisplayString = (
    configuredStatisticsConfigurations: StatisticsConfiguration[]
): string =>
    configuredStatisticsConfigurations
        .map((statisticsConfiguration) => statisticsConfiguration.ID)
        // Remove duplicates if any.
        .filter(
            (statisticsConfigurationID, index, configuredStatisticsConfigurationsIDArray) =>
                configuredStatisticsConfigurationsIDArray.indexOf(statisticsConfigurationID) === index
        )
        .join(STATISTICS_SETTINGS_LIST_SEPARATOR)

export const mapStatisticsConfigurationsToRequestStatisticsSettingsString = (
    configuredStatisticsConfigurations: StatisticsConfiguration[],
    currencyFormatter: CDecimalFormatter
): string =>
    mapStatisticsConfigurationsToStatisticsSettingsString(configuredStatisticsConfigurations, currencyFormatter)

export /**
 * Converts list of report list items with no hierarchy to list of items with links to parent item using its
 * identifier.
 * @param reportsFromResponse reports to render.
 */
const toReportListItems = (reportsFromResponse: ReportListItemResponse[]): ReportListItemType[] => {
    // const reportListItemsMapByID: ReportListItemsIndexByReportID = new Map()
    const reportListItems: ReportListItemType[] = []

    for (let i = 0; i < reportsFromResponse.length; i += 1) {
        // Build array of file paths.
        const description = reportsFromResponse[i].Description
        const filePath = description.split(FOLDERS_SEPARATOR_BACKEND)
        let currentPath = ''
        let parentPath
        for (let j = 0; j < filePath.length; j += 1) {
            parentPath = currentPath === '' ? undefined : currentPath
            currentPath = `${currentPath}${j > 0 ? FOLDERS_SEPARATOR_BACKEND : ''}${filePath[j]}`
            if (j === filePath.length - 1) {
                // This element of the path is now the report name.
                const reportTitle = filePath[j]
                // Maps and stores certain properties from report response to internal structure.
                reportListItems.push({
                    isReport: true,
                    id: reportsFromResponse[i].ReportID,
                    name: reportTitle,
                    duration: reportsFromResponse[i].Duration,
                    rptType: reportsFromResponse[i].ReportType,
                    groupingViewType: reportsFromResponse[i].GroupingViewType,
                    supportsIconViews: reportsFromResponse[i].SupportsIconsViews,
                    supportsCharting: reportsFromResponse[i].SupportsCharting,
                    supportsRowHighlighting: reportsFromResponse[i].SupportsRowHighlighting,
                    viewType: reportsFromResponse[i].ViewType,
                    WBTileType: reportsFromResponse[i].WBTileType,
                    filterDevices: reportsFromResponse[i].FilterDevices,
                    filterDevicesMax: reportsFromResponse[i].FilterDevicesMax,
                    userLicensedFor: reportsFromResponse[i].UserLicensedFor,
                    isLicensed: reportsFromResponse[i].IsLicensed,
                    isLicensedRealTime: reportsFromResponse[i].IsLicensedRealTime,
                    isScheduleActive: reportsFromResponse[i].IsScheduleActive,
                    isAutoSwitched: reportsFromResponse[i].IsAutoSwitched,
                    autoSwitchIntervalDisplay: reportsFromResponse[i].AutoSwitchIntervalDisplay,
                    parentFolderID: filePath.length === 1 ? undefined : parentPath,
                    description,
                    isBinned: reportsFromResponse[i].IsBinned,
                    daysUntilDeletion: reportsFromResponse[i].DaysUntilDeletion
                })
            } else {
                // This element of the path is a folder name.
                const folderName = filePath[j]
                // TODO: maybe use map here to avoid checking
                const folderReportListItemId = currentPath
                const folderItemAlreadyExists = !IsVarEmpty(
                    reportListItems.find(
                        (reportListItem) =>
                            reportListItem.id === folderReportListItemId &&
                            reportListItem.isBinned === reportsFromResponse[i].IsBinned
                    )
                )

                if (!folderItemAlreadyExists) {
                    // Maps and stores certain properties from report response to internal structure.
                    reportListItems.push({
                        isReport: false,
                        id: currentPath,
                        name: folderName,
                        parentFolderID: filePath.length === 1 ? undefined : parentPath,
                        description: currentPath,
                        isBinned: reportsFromResponse[i].IsBinned,
                        daysUntilDeletion: -1
                    })
                }
            }
        }
    }

    return reportListItems
}
